import useSWR, { Fetcher } from 'swr';

import { deepCamelCase, NetworkError } from '@vimeo/gctl-api/fetchers/_helpers';
import { useViewer } from '@vimeo/viewer';
import { getSearchParams } from '@vimeo/viewer-showcase-ui/utils/helper';

export const ShowcaseAuthErrorCode = {
  passwordPrivacy: 0,
  incorrectPassword: 1,
  teamPrivacy: 2,
  teamPrivacyLoggedInUser: 3,
  embedOnly: 4,
  unknownReason: 5,
  unlisted: 6,
} as const;

/**
 * Metadata is used to render alternative showcase views, e,g BrandedLoginPage
 * It's coming from `Album/AuthorizationController::authorize`
 */
export interface AlbumMetadata {
  /** album title, e.g 'New showcase' */
  name: string;
  /** Album accent color config, e.g 'efa700' */
  brandColor: string | null;
  /** Album owner id, e.g 105616991 */
  creator: number;
  /** ? this might be never set ? */
  url: string | null;
  /** ?offsite domain */
  domain: string | null;
  /** Album custom logo url */
  customLogo: string | null;
  /** Whether Album support SSO Login */
  hasSso: boolean;
  /** Album theme config, if it's not dark, it's locked in light theme */
  theme: 'standard' | 'dark';
  /** Album id, e.g 11421939 */
  id: number;
  /** Album owner's SSO connection counts */
  ssoConnectionsCount: number;
  /** Album layout config */
  layout: string | null;
}

export interface ShowcaseAuthResponse {
  errorCode: typeof ShowcaseAuthErrorCode[keyof typeof ShowcaseAuthErrorCode] | null;
  metadata: AlbumMetadata;
  hashedPass?: string | null; // won't be included in the response if it's non 200
}

export interface ShowcaseAuthError {
  res: ShowcaseAuthResponse;
  status: number;
}

export const useShowcaseAuth = (
  albumIdOrUrl: string | number,
  options: { isShowcaseEmbedded?: boolean },
) => {
  const viewer = useViewer();
  const sh = getSearchParams().get('sh'); // album hash

  return useSWR<ShowcaseAuthResponse, ShowcaseAuthError>(
    () => {
      if (!viewer) return null;

      const authQueryParams = {
        ...(sh && { sh }),
        ...(options?.isShowcaseEmbedded && {
          referrer: encodeURIComponent(getReferrerValue() || ''),
          is_embed: true,
        }),
      };

      /**
       * This is not Vimeo API endpoint, hence no GCTL hook generated, use SWR directly
       * Ref: https://github.com/vimeows/vimeo/blob/f054bd8cee34386203217ef0b28718bcef8b53f8/routes/main.php#L4565-L4569
       */
      const authUrl = albumIdOrUrl
        ? `/showcase/${albumIdOrUrl}/auth${serializeQuery(authQueryParams)}`
        : '/auth'; // For showcase custom domain, such as "http://vodcontainer.com/", there won't be a albumIdOrUrl

      return authUrl;
    },
    showcaseAuthFetcher,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );
};

const showcaseAuthFetcher: Fetcher<ShowcaseAuthResponse, string> = async (url) => {
  const res = await fetch(url);

  if (res.status === 404) {
    throw new NetworkError('Not found', res.status, res);
  }

  if (!res.ok) {
    const errorRes = await res.json();

    throw new NetworkError('A network error occurred', res.status, deepCamelCase(errorRes));
  }

  const json = await res.json();

  return deepCamelCase(json);
};

const serializeQuery = (options: Record<string, unknown>) => {
  const res = Object.entries(options)
    .filter(([, value]) => typeof value !== 'undefined')
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return res ? `?${res}` : '';
};

const getReferrerValue = () => {
  try {
    if (document?.referrer) {
      return document.referrer;
    }

    return document?.querySelector('meta[name="referrer"]')?.getAttribute('content') || '';
  } catch (error) {
    return '';
  }
};
